import type { VisualizationSpec } from "vega-embed";
import type { ChartFormatValues } from "../../components/registry";
import {
  AXIS_KEYS,
  MEASURE_CHANNELS,
  isMeasureDefinition,
  readChartFormat,
} from "./readChartFormat";
import { numberFormatSpecifier } from "./chartNumberFormats";
import { legendOrient } from "./legendPosition";

type SpecObject = Record<string, unknown>;
type FormatKey = keyof ChartFormatValues;

/**
 * Writes the Format tab's values back into a chart's Vega-Lite spec — the
 * writer half of the spec adapter whose reader half is `readChartFormat`, and
 * the sibling of `applyPaletteToSpec`, which owns colour alone.
 *
 * **It writes deltas, never the whole value set.** The reason is the reader's
 * `Partial` contract: a spec that states nothing about, say, gridlines leaves
 * that key to `chartFormatDefaults`, and the default is a *display* seed, not a
 * fact about the chart. Writing the full value set would therefore stamp
 * registry fixtures onto every chart the moment it is opened. So the baseline
 * is recomputed from the chart's own spec here (`defaults` + what the spec
 * states, exactly as `readChartFormatEntry` seeds it), and only keys the user
 * actually moved away from that baseline are written. An untouched chart gets
 * its original spec object straight back.
 *
 * chartTitle/showChartTitle are the one pair of Format-tab keys this writer
 * never touches — see the comment above their (absent) block below.
 *
 * Two spec shapes are supported — a unit spec and a `layer` spec — matching the
 * pair the reader walks and `ChartWidget`'s sizing pass already handles.
 */

const asObject = (value: unknown): SpecObject | undefined =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as SpecObject)
    : undefined;

/** Mirrors the reader's `unitSpecs`: the leaves that can carry an `encoding`. */
const mapUnits = (spec: SpecObject, edit: (unit: SpecObject) => SpecObject): SpecObject => {
  if (!Array.isArray(spec.layer)) return edit(spec);
  return {
    ...spec,
    layer: (spec.layer as unknown[]).map((layer) => {
      const unit = asObject(layer);
      return unit ? edit(unit) : layer;
    }),
  };
};

/** Rewrites one encoding channel, leaving a unit that lacks it untouched. */
const editChannel = (
  unit: SpecObject,
  channel: string,
  edit: (definition: SpecObject) => SpecObject,
): SpecObject => {
  const encoding = asObject(unit.encoding);
  const definition = encoding && asObject(encoding[channel]);
  if (!encoding || !definition) return unit;
  return { ...unit, encoding: { ...encoding, [channel]: edit(definition) } };
};

const markType = (unit: SpecObject): string | undefined => {
  if (typeof unit.mark === "string") return unit.mark;
  const type = asObject(unit.mark)?.type;
  return typeof type === "string" ? type : undefined;
};

/**
 * The keys whose value no longer matches what the chart's own spec says.
 *
 * Exported for the same reason it exists: callers (and tests) need to be able
 * to prove that opening a chart and changing nothing writes nothing.
 */
export const changedFormatKeys = (
  spec: VisualizationSpec,
  values: ChartFormatValues,
  defaults: ChartFormatValues,
  fallbackTitle?: string,
): Set<FormatKey> => {
  const baseline: ChartFormatValues = { ...defaults, ...readChartFormat(spec, fallbackTitle) };
  const changed = new Set<FormatKey>();
  (Object.keys(values) as FormatKey[]).forEach((key) => {
    if (values[key] !== baseline[key]) changed.add(key);
  });
  return changed;
};

const AXIS_CHANNELS = ["x", "y"] as const;

/**
 * One axis channel's four settings. `axis: null` is Vega-Lite's "hide this
 * axis", so turning the axis off short-circuits: nothing else on a hidden axis
 * is observable, and writing a title under a `null` would be dead spec.
 */
const writeAxis = (
  definition: SpecObject,
  channel: "x" | "y",
  values: ChartFormatValues,
  changed: Set<FormatKey>,
): SpecObject => {
  const keys = AXIS_KEYS[channel];
  if (changed.has(keys.show) && !values[keys.show]) return { ...definition, axis: null };

  // Spreading a missing (or `null`) axis into `{}` is also how the axis comes
  // back: re-enabling `showXAxis` replaces `axis: null` with a real object.
  const axis: SpecObject = { ...(asObject(definition.axis) ?? {}) };
  const next: SpecObject = { ...definition, axis };

  if (changed.has(keys.showTitle) || changed.has(keys.title)) {
    // A channel-level `title` outranks `axis.title` in Vega-Lite, so it has to
    // go — otherwise the panel's edit is written and silently overridden.
    delete next.title;
    axis.title = values[keys.showTitle] ? values[keys.title] : null;
  }
  if (changed.has(keys.angle)) axis.labelAngle = values[keys.angle];
  if (changed.has(keys.grid)) axis.grid = values[keys.grid];
  // "Category names on the chart" — the x-axis labels, matching the side the
  // reader answers this toggle from.
  if (channel === "x" && changed.has("showLabels")) axis.labels = values.showLabels;

  return next;
};

/** Whether any of a channel's keys moved, so an untouched axis is left alone. */
const axisTouched = (channel: "x" | "y", changed: Set<FormatKey>): boolean => {
  const keys = AXIS_KEYS[channel];
  return (
    changed.has(keys.show) ||
    changed.has(keys.showTitle) ||
    changed.has(keys.title) ||
    changed.has(keys.angle) ||
    changed.has(keys.grid) ||
    (channel === "x" && changed.has("showLabels"))
  );
};

const writeLegend = (
  definition: SpecObject,
  values: ChartFormatValues,
  changed: Set<FormatKey>,
): SpecObject => {
  // Position is a dependent setting: a stale/persisted position delta must
  // never recreate a legend while the parent visibility toggle is off.
  if (!values.showLegends) return { ...definition, legend: null };

  // The orient is written on every pass this function makes, not only when the
  // position key itself moved. Gating it on the diff is what let a picked
  // position be a silent no-op: the panel's default used to disagree with
  // Vega's, so choosing the default-looking button produced no change to write
  // and Vega drew the legend wherever ITS default put it. Writing the value the
  // picker is showing means the button and the canvas cannot disagree.
  const legend: SpecObject = { ...(asObject(definition.legend) ?? {}) };
  legend.orient = legendOrient(values.legendPosition);
  return { ...definition, legend };
};

const legendTouched = (changed: Set<FormatKey>): boolean =>
  changed.has("showLegends") || changed.has("legendPosition");

/**
 * Tooltips are a mark property, so a string shorthand mark has to be expanded
 * to its object form first. Turning them off also drops any `tooltip` encoding
 * channel: the two are alternatives in Vega-Lite, and the channel would keep
 * the tooltip alive on its own.
 */
const writeTooltip = (unit: SpecObject, enabled: boolean): SpecObject => {
  const type = markType(unit);
  if (!type) return unit;
  const mark: SpecObject = { ...(asObject(unit.mark) ?? {}), type, tooltip: enabled };
  const next: SpecObject = { ...unit, mark };
  const encoding = asObject(unit.encoding);
  if (!enabled && encoding && "tooltip" in encoding) {
    const { tooltip: _dropped, ...rest } = encoding;
    next.encoding = rest;
  }
  return next;
};

/**
 * Which channel of THIS unit carries the numbers, using the same "is it a
 * measure" rule the reader applies. Per unit rather than per spec, because a
 * layered spec can put its measure on a different channel in each layer.
 */
const measureChannel = (unit: SpecObject): "y" | "x" | undefined => {
  const encoding = asObject(unit.encoding);
  if (!encoding) return undefined;
  return MEASURE_CHANNELS.find((channel) => isMeasureDefinition(asObject(encoding[channel])));
};

/**
 * One d3 specifier reaches two places: the measure axis's tick labels, and the
 * numbers a direct-value text layer prints. Writing only the axis would leave a
 * chart showing "$5M" on the axis and "5000000" on the bar.
 *
 * A hidden axis (`axis: null`) is skipped rather than spread into an object —
 * formatting ticks nobody can see is not worth un-hiding the axis for.
 */
const writeNumberFormat = (unit: SpecObject, format: string): SpecObject => {
  let next = unit;
  const channel = measureChannel(unit);
  if (channel) {
    next = editChannel(next, channel, (definition) =>
      definition.axis === null
        ? definition
        : { ...definition, axis: { ...(asObject(definition.axis) ?? {}), format } },
    );
  }
  const encoding = asObject(next.encoding);
  const text = encoding && asObject(encoding.text);
  if (encoding && text) {
    next = { ...next, encoding: { ...encoding, text: { ...text, format } } };
  }
  return next;
};

/** Figma prints direct values just clear of the mark; negative dy is upward. */
const DIRECT_VALUE_OFFSET = -8;

const isDirectValueLayer = (unit: SpecObject): boolean =>
  markType(unit) === "text" && asObject(unit.encoding)?.text !== undefined;

/**
 * Builds the text layer that prints a number on each mark, from the measure
 * channel the chart already encodes — y first, because every chart the canvas
 * renders today puts its measure there, x only as a fallback for a horizontal
 * bar. Axis and scale config is stripped: they describe the axis the number
 * came from, not the label being drawn.
 *
 * `color` is dropped so the labels stay in Vega's default dark text rather than
 * taking the series colour, which a pale palette would render unreadable.
 */
const directValueLayer = (unit: SpecObject): SpecObject | undefined => {
  const encoding = asObject(unit.encoding);
  if (!encoding) return undefined;
  const measure = asObject(encoding.y) ?? asObject(encoding.x);
  if (!measure) return undefined;
  const { axis: axisConfig, scale: _scale, legend: _legend, ...text } = measure;
  const { color: _color, ...position } = encoding;
  // The axis is stripped, but its d3 specifier is not: the printed number and
  // the tick it sits above have to read the same way.
  const format = asObject(axisConfig)?.format;
  return {
    mark: { type: "text", dy: DIRECT_VALUE_OFFSET },
    encoding: { ...position, text: typeof format === "string" ? { ...text, format } : text },
  };
};

/**
 * Numbers on the marks are their own text layer in Vega-Lite, so this is the
 * one setting that changes a spec's *shape* rather than one of its properties:
 * switching it on wraps a unit spec into a two-layer spec, and switching it off
 * removes the text layers again.
 */
const writeDirectValues = (root: SpecObject, enabled: boolean): SpecObject => {
  if (!enabled) {
    if (!Array.isArray(root.layer)) return root;
    const layers = (root.layer as unknown[]).filter((layer) => {
      const unit = asObject(layer);
      return !unit || !isDirectValueLayer(unit);
    });
    return layers.length === (root.layer as unknown[]).length ? root : { ...root, layer: layers };
  }

  if (Array.isArray(root.layer)) {
    const units = (root.layer as unknown[])
      .map(asObject)
      .filter((unit): unit is SpecObject => unit !== undefined);
    if (units.some(isDirectValueLayer)) return root;
    const base = units.find((unit) => asObject(unit.encoding) !== undefined);
    const layer = base && directValueLayer(base);
    return layer ? { ...root, layer: [...(root.layer as unknown[]), layer] } : root;
  }

  const layer = directValueLayer(root);
  if (!layer) return root;
  // The mark and encoding move down into the first layer; data, title, sizing
  // and config stay at the root, where a layer spec still expects them.
  const { mark, encoding, ...shared } = root;
  return { ...shared, layer: [{ mark, encoding }, layer] };
};

/**
 * Applies everything the Format tab owns except colour, which
 * `applyPaletteToSpec` writes from the palette library instead.
 *
 * Returns the given spec object unchanged when nothing was edited, so the
 * canvas memo can keep rendering the backend's original spec by identity.
 */
export const applyChartFormatToSpec = (
  spec: VisualizationSpec,
  values: ChartFormatValues,
  defaults: ChartFormatValues,
  fallbackTitle?: string,
): VisualizationSpec => {
  const root = asObject(spec);
  if (!root) return spec;

  const changed = changedFormatKeys(spec, values, defaults, fallbackTitle);
  if (changed.size === 0) return spec;

  let next = root;

  // chartTitle/showChartTitle are deliberately NOT written here. The chart
  // title a viewer actually sees is the widget's own header (ChartWidget.tsx's
  // `<Title>`), wired directly from Chart Studio state in
  // useChartStudioFormat.ts's `displayWidgets`. Writing the same text into
  // the spec's `title` too would make Vega paint a second, redundant title
  // inside the chart canvas, underneath the real one.

  const touchedAxes = AXIS_CHANNELS.filter((channel) => axisTouched(channel, changed));
  const touchesLegend = legendTouched(changed);
  const touchesTooltip = changed.has("showTooltip");

  // Currency and Percentage are two menus over ONE spec property — the measure
  // axis's d3 specifier — so they cannot both apply. The one just picked wins;
  // the other row keeps showing its sample until it is picked in turn.
  const numberFormat = changed.has("percentage")
    ? numberFormatSpecifier(values.percentage)
    : changed.has("currency")
      ? numberFormatSpecifier(values.currency)
      : undefined;

  if (touchedAxes.length > 0 || touchesLegend || touchesTooltip || numberFormat) {
    next = mapUnits(next, (unit) => {
      let edited = unit;
      touchedAxes.forEach((channel) => {
        edited = editChannel(edited, channel, (definition) =>
          writeAxis(definition, channel, values, changed),
        );
      });
      if (touchesLegend) {
        edited = editChannel(edited, "color", (definition) =>
          writeLegend(definition, values, changed),
        );
      }
      if (touchesTooltip) edited = writeTooltip(edited, values.showTooltip);
      if (numberFormat) edited = writeNumberFormat(edited, numberFormat);
      return edited;
    });
  }

  if (changed.has("showDirectValues")) {
    next = writeDirectValues(next, values.showDirectValues);
  }

  return next as VisualizationSpec;
};

/* ---- Label fit: collisions are handled by width, never by silently dropping ---- */

const LABEL_CHAR_PX = 6.5;
const LABEL_SLOT_PADDING_PX = 72;
const MIN_ROTATED_SLOT_PX = 14;
const MAX_LABEL_LIMIT_PX = 140;
const VALUE_LABEL_MIN_SLOT_PX = 30;

const inlineRows = (root: SpecObject): SpecObject[] => {
  const values = asObject(root.data)?.values;
  return Array.isArray(values) ? values.map(asObject).filter((row): row is SpecObject => row !== undefined) : [];
};

/** The discrete x channel of the first unit that has one, with its inline category names. */
const categoryLabels = (root: SpecObject): string[] | undefined => {
  const units = Array.isArray(root.layer) ? (root.layer as unknown[]).map(asObject) : [root];
  for (const unit of units) {
    const x = asObject(asObject(unit?.encoding)?.x) ?? asObject(asObject(root.encoding)?.x);
    if (!x || (x.type !== "nominal" && x.type !== "ordinal") || x.axis === null) continue;
    if (typeof x.field !== "string") continue;
    const field = x.field;
    return Array.from(new Set(inlineRows(root).map((row) => String(row[field] ?? ""))));
  }
  return undefined;
};

/**
 * Lays x-axis category names and printed values out for a measured chart width.
 *
 * A name that does not fit its slot is first rotated, then ellipsised at the
 * slot, so the reader still gets every label (the tooltip carries the full
 * name). Only a chart with more categories than the narrowest rotated slot
 * allows falls back to `labelOverlap: "greedy"`. A spec's own axis angle,
 * limit and overlap settings always win. Returns the same object when nothing
 * needs to change (no inline categories, unmeasured width, or everything fits).
 */
export const withLabelFit = (spec: VisualizationSpec, widthPx: number): VisualizationSpec => {
  const root = asObject(spec);
  if (!root || !(widthPx > 0)) return spec;
  const labels = categoryLabels(root);
  if (!labels || labels.length === 0) return spec;

  const slot = Math.max(0, widthPx - LABEL_SLOT_PADDING_PX) / labels.length;
  const longest = Math.max(...labels.map((label) => label.length)) * LABEL_CHAR_PX;
  const needsRoom = longest > slot;
  const valueLabelsNeedRoom = slot < VALUE_LABEL_MIN_SLOT_PX;
  if (!needsRoom && !valueLabelsNeedRoom) return spec;

  const rotate = needsRoom ? (slot >= MIN_ROTATED_SLOT_PX ? -45 : -90) : undefined;
  const limit = Math.round(Math.min(MAX_LABEL_LIMIT_PX, Math.max(48, rotate ? longest : slot)));

  return mapUnits(root, (unit) => {
    if (isDirectValueLayer(unit)) {
      if (!valueLabelsNeedRoom) return unit;
      const mark = asObject(unit.mark) ?? { type: "text" };
      return {
        ...unit,
        mark: {
          fontSize: 9,
          ...(slot < 20 ? { angle: 270, align: "left", baseline: "middle" } : {}),
          ...mark,
        },
      };
    }
    if (!needsRoom) return unit;
    return editChannel(unit, "x", (definition) => {
      if (definition.axis === null) return definition;
      const axis = asObject(definition.axis) ?? {};
      return {
        ...definition,
        axis: {
          labelAngle: rotate,
          labelLimit: limit,
          labelOverlap: slot >= MIN_ROTATED_SLOT_PX ? false : "greedy",
          ...axis,
        },
      };
    });
  }) as VisualizationSpec;
};
